import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from '@angular/material/table';
import { AemDocumentSearchService } from "./aem-document-search.service";
import { AEMDocument } from "./interfaces/AEM-document-retrieval.interface";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { Tag, TagOption } from "./interfaces/tag.interface";
import { CONTROL_TYPE_ENUM } from "./enums/control-type.enum";

interface RequiredTagsFormControls {
  [key: string]: AbstractControl;
}
interface RequiredTagsFormControlsAsJson {
  [key: string]: string | string[];
}
interface FilterTagsFormControls {
  [key: string]: AbstractControl;
}
interface FilterTagsFormControlsAsJson {
  [key: string]: string | string[];
}
interface TextFilterFormControls {
  fileSearch: AbstractControl;
}
interface TextFilterFormControlsAsJson {
  fileSearch: string;
}

interface FilterConfiguration {
  retrievalTags: Tag[];
  filterTags: Tag[];
  docRoot: string;
}

@Component({
    selector: 'app-aem-document-search',
    templateUrl: './aem-document-search.component.html',
    styleUrls: ['./aem-document-search.component.scss'],
    standalone: false
})
export class AemDocumentSearchComponent implements OnInit, OnDestroy {

  @Input() retrievalTags: string;
  @Input() filterTags: string;
  @Input() docRoot: string; // Location in AEM DAM where documents will be searched for
  @Input() servicePath: string;

  configuration: FilterConfiguration;
  _backupConfiguration: FilterConfiguration;

  @Input() manuallyTriggerSearch: string = 'true'
  manuallyTriggeredSearch: boolean;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("AemDocumentSearchPaginator") paginator: MatPaginator;
  /** Data Source + Configs */
  dataSet: AEMDocument[];
  dataSource: MatTableDataSource<AEMDocument>;
  _backupDataSource: MatTableDataSource<AEMDocument>;
  displayedColumns = [
    'fileName',
    'download'
  ];

  isRetrievalTriggered: boolean;

  formSubmissionAttempted: boolean;
  requiredTagsForm: FormGroup;
  requiredTagsFormChangesSubscription: Subscription;
  filterTagsForm: FormGroup;
  filterTagsFormChangesSubscription: Subscription;
  textFilterForm: FormGroup;
  textFilterFormMinimumReqs = {
    length: 3
  }
  fileSearchSubscription: Subscription;

  displayFilterTags: boolean;

  constructor(
    private aemDocumentSearchService: AemDocumentSearchService,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  get requiredTagsFormControls(): RequiredTagsFormControls {
    return this.requiredTagsForm.controls as unknown as RequiredTagsFormControls;
  }
  get requiredTagsFormValues(): RequiredTagsFormControlsAsJson {
    return this.requiredTagsForm.value as unknown as RequiredTagsFormControlsAsJson;
  }
  get filterTagsFormControls(): FilterTagsFormControls {
    return this.filterTagsForm.controls as unknown as FilterTagsFormControls;
  }
  get filterTagsFormValues(): FilterTagsFormControlsAsJson {
    return this.filterTagsForm.value as unknown as FilterTagsFormControlsAsJson;
  }
  get textFilterFormControls(): TextFilterFormControls {
    return this.textFilterForm.controls as unknown as TextFilterFormControls;
  }
  get textFilterFormValues(): TextFilterFormControlsAsJson {
    return this.textFilterForm.value as unknown as TextFilterFormControlsAsJson;
  }

  ngOnInit() {

    this.initializeConfigurationSetup();

    this.manuallyTriggeredSearch = this.manuallyTriggerSearch === 'true';

    this.requiredTagsForm = this.fb.group({});
    this.configuration.retrievalTags.forEach(t => {
      this.requiredTagsForm.addControl(t.label, this.fb.control('', [Validators.required]));
    });
    this.requiredTagsFormChangesSubscription = this.requiredTagsForm.valueChanges.subscribe(v => {
      this.requiredTagsForm.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });

    this.filterTagsForm = this.fb.group({});
    this.configuration.filterTags.forEach(t => {
      this.filterTagsForm.addControl(t.label, this.fb.control(''));
      if (t.subordinateTo) {
        this.filterTagsFormControls[t.label].disable();
      }
    });
    this.filterTagsFormChangesSubscription = this.filterTagsForm.valueChanges.subscribe(v => {
      this.filterTagsForm.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });

    this.textFilterForm = this.fb.group({
      fileSearch: this.fb.control('')
    });
    this.fileSearchSubscription = this.textFilterFormControls.fileSearch.valueChanges.subscribe(v => {
      if (v.length === 0 || v.length >= this.textFilterFormMinimumReqs.length) {
        this.textFilterForm.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        this.onFilterChange();
      }
    });

  }

  ngOnDestroy() {
    if (this.fileSearchSubscription) {
      this.fileSearchSubscription.unsubscribe();
    }
    if (this.requiredTagsFormChangesSubscription) {
      this.requiredTagsFormChangesSubscription.unsubscribe();
    }
    if (this.filterTagsFormChangesSubscription) {
      this.filterTagsFormChangesSubscription.unsubscribe();
    }
  }

  initializeConfigurationSetup() {
    this.configuration = {
      retrievalTags: this.parseTagConfiguration(this.retrievalTags),
      filterTags: this.parseTagConfiguration(this.filterTags),
      docRoot: this.docRoot
    }
    this._backupConfiguration = JSON.parse(JSON.stringify(this.configuration));
  }

  parseTagConfiguration(aemString: string): Tag[] {

    const returnableTags = [];

    const y: {
      tagLabel: string,
      tags: string[],
      subordinateTo: string,
      controlType: CONTROL_TYPE_ENUM
    }[] = JSON.parse(aemString);

    y.forEach(z => {

      const tag: Tag = {
        label: z.tagLabel,
        options: this.parseOptionsFromTags(z.tags, z.controlType),
        field: this.getTagFieldNameFromOptions(z.tags),
        subordinateTo: z.subordinateTo || null,
        tag: z.tagLabel,
        type: z.controlType
      };

      returnableTags.push(tag);
    })

    return returnableTags;

  }

  // Trigger request to external service based on populated retrieval tags
  async checkRetrievalTags() {

    this.formSubmissionAttempted = true;

    Object.keys(this.requiredTagsFormControls).forEach(cName => this.requiredTagsFormControls[cName].updateValueAndValidity());

    /** Retrieval tags have to have a value ( or values ) */
    const populatedTags = Object.keys(this.requiredTagsFormControls).filter(cName => this.requiredTagsFormValues[cName] && this.requiredTagsFormValues[cName].length > 0);

    /** Compare populated tags w/ total tags */
    this.isRetrievalTriggered = populatedTags.length === this.configuration.retrievalTags.length;

    /** Can do request */
    if (this.isRetrievalTriggered) {

      /** Get tags which are not undefined/null */
      const retrievalTagValuesArray = Object.keys(this.requiredTagsFormControls).map(cName => {
        const value = this.requiredTagsFormValues[cName];
        if (this.valueIsNotNullOrEmpty(value)) { return value; }
      });

      // Unique
      const flattenedArray = retrievalTagValuesArray.flat();

      // Filter Form reset
      this.filterTagsForm.reset()

      // Retrieve Data
      this.getDocuments(flattenedArray);

      this.displayFilterTags = this.configuration.filterTags.some(this.hasValuesToDisplay);

    } else {
      this.dataSource = null;
    }
  }

  async getDocuments(tagArray: string[]): Promise<void> {

    // Retrieve Data
    const damResponse: AEMDocument[] =
      await this.aemDocumentSearchService.getDamData(
        this.servicePath,
        this.docRoot,
        tagArray
      );

    // Save to DataStore
    this.transformXHRDataToDataSource(damResponse);

  }

  isSubordinateTagVisible(tag: Tag): boolean {
    if (tag.subordinateTo) {
      const parentTag = this.configuration.filterTags.find((t: Tag) => t.tag === tag.subordinateTo);
      return !!this.valueIsNotNullOrEmpty(this.filterTagsFormValues[parentTag.label]);
    };
    return true;
  }

  hasValuesToDisplay(tag: Tag): boolean {
    return tag.options.some(o => !o.isHiddenBySubordinateLogic);
  }

  updateCheckboxList(selectedOption: string, tag: Tag, form: FormGroup): boolean {

    const control = form.get(tag.label);
    if (!control.value) {
      control.setValue([]);
    }

    let newVal = null;
    if (control.value.indexOf(selectedOption) !== -1) {
      newVal = control.value.filter(v => v !== selectedOption);
      control.setValue(newVal);
    } else {
      newVal = control.value;
      newVal.push(JSON.parse(JSON.stringify(selectedOption)));
      control.setValue(newVal);
    }

    return true;
  }

  configureFilterPredicate() {
    if (this.dataSource) {

      this.dataSource.filterPredicate = (data: AEMDocument, filter: string) => {

        const activeFilterTags = this.configuration.filterTags.filter((tag: Tag) => this.filterTagsFormValues[tag.label] && this.filterTagsFormValues[tag.label].length > 0);
        const textFilterSearch = this.textFilterFormValues.fileSearch?.length >= this.textFilterFormMinimumReqs.length;

        /** No filters active, show it all */
        if (activeFilterTags.length === 0 && !textFilterSearch) {
          return true;
        }

        /** Gets a new entry when a FILTER TAG is found ON THE DOCUMENT */
        const numberOfMatches = [];

        /** Some filters active, filter */
        for (const filterTag of activeFilterTags) {

          /** Field and Label are currently the same; but Label is displayed and Field is used to match TAG-bucket with FORM-selected values */

          // DOCUMENTS - from service
          /** Tag type */
          const lowercaseField = filterTag?.field?.toLocaleLowerCase();
          /** Tag values */
          let lowercaseFieldValues = (data && data[lowercaseField] as string[])?.map(v => v?.toLocaleLowerCase().split(' ').join('-'));

          // FORM - values selected by user
          if (Array.isArray(this.filterTagsFormValues[filterTag.label])) {
            /** Tag(s) selected */
            const lowerCaseValues = (this.filterTagsFormValues[filterTag.label] as string[])?.map(v => v.toLocaleLowerCase());
            if (lowerCaseValues && lowercaseFieldValues?.some(docSingleTag => lowerCaseValues.some(selectedFilter => selectedFilter.includes(`/${docSingleTag}`)))) {
              numberOfMatches.push(true);
            }
          } else {
            /** Tag selected */
            const lowercaseValue = (this.filterTagsFormValues[filterTag.label] as string).toLocaleLowerCase();
            if (lowercaseValue && lowercaseFieldValues?.includes(lowercaseValue.substring(lowercaseValue.lastIndexOf('/') + 1, lowercaseValue.length))) {
              numberOfMatches.push(true);
            }
          }

        }

        /**
         * Compare the number of MATCHED FILTER TAGS with TOTAL FILTER TAGS - if they are equal then we have a good match;
         * Do text search
         */
        if (numberOfMatches.length === activeFilterTags.length) {
          if (this.textFilterFormValues.fileSearch) {
            return data.fileName?.toLocaleLowerCase()?.includes(this.textFilterFormValues.fileSearch?.toLocaleLowerCase());
          }
          return true;
        }

        return false;
      };

    }
  }

  transformXHRDataToDataSource(xhrData: AEMDocument[]) {

    // Transform XHR service data to a generic MatTableDataSource
    this.dataSource = new MatTableDataSource<AEMDocument>(xhrData);
    this._backupDataSource = new MatTableDataSource<AEMDocument>(xhrData);

    // Setup filter
    this.configureFilterPredicate();

    // Trigger filter
    this.onFilterChange();

    /** Setup Sort */
    this.dataSource.sort = this.sort;

    /** Setup Paginator */
    this.dataSource.paginator = this.paginator;

  }

  /** Invoked from HTML */
  onFilterChange(tag?: Tag, index?: number) {

    // unset form values that are dependent on this one after a change.....
    if (tag) {
      this.resetSubordinateTagsFormControls(tag);
      this.changeDetectorRef.detectChanges();
    }

    // trigger filterPredicate ( do this after resets to ensure new filter data is post-reset)
    if (this.dataSource) {
      this.dataSource.filter = '';
      this.dataSource.filter = 'DO FILTER!';
    }

    // update the options, given updated filterPredicate
    this.updateFilterTagsDisplay(index);

    this.updateControlDisableEnable();
  }

  updateControlDisableEnable() {
    this.configuration.filterTags.forEach( tag => {
      if (
        (!tag.subordinateTo)
        || (
          this.isSubordinateTagVisible(tag) &&
          this.filterTagsFormValues[tag.subordinateTo] &&
          this.filterTagsFormValues[tag.subordinateTo].length > 0
        )
        && this.hasValuesToDisplay(tag)
      ) {
        this.filterTagsFormControls[tag.label].enable();
      } else {
        this.filterTagsFormControls[tag.label].disable();
      }
    })
  }

  resetSubordinateTagsFormControls(tag: Tag) {
    const tags = this.configuration.filterTags;
    for (let i = 0; i < tags.length; i++) {
      // this control is subordinate to the changed control
      if (tags[i].subordinateTo === tag.label) {
        this.resetSubordinateTagsFormControls(tags[i]);
        this.filterTagsFormControls[tags[i].label].reset();
      }
    }
  }

  updateFilterTagsDisplay(index: number) {

    function onlyUnique(value, index, array) {
      return array.indexOf(value) === index;
    }

    /**
     * The values attached to docs don't contain the lower-casey / hyphenated values
     */
    function normalizeTagString(t: string) {
      if (t) {
        return t.replace(/-/g, ' ').toLocaleLowerCase();
      }
    };

    // Given 2 indices, one which is the filter selected, return true when comparing a filter appearing later than the interacted with one
    // Observe: Y is selected; o is true; x is false;
    // [x][x][x][Y][o][o]
    function neitherSelfNorFirstNorEarlierTag(filterIndex: number, selectedFilterIndex: number): boolean {
      return (!selectedFilterIndex && selectedFilterIndex !== 0) || filterIndex > selectedFilterIndex;
    };

    // Maybe if the recent change has a tag.subordinateTo, we don't update the allActiveTags[t.field] with data from the new filter.....
    const filteredData = this.dataSource.filteredData;
    const originalData = this._backupDataSource.data;

    const allActiveTags = {};

    /**
     * Create map which captures ACTIVE TAGS on FILTERED DOCUMENTS
     */
    this.configuration.filterTags.forEach((t, i) => {

      allActiveTags[t.field] = [];

      // Use filtered data if the ITERABLE comes AFTER an ALREADY-SELECTED field
      // Use original data if the ITERABLE comes before and isn't yet selected
      // Basically - we are universally updating options, but don't want to filter previous selections
      let dataValues = neitherSelfNorFirstNorEarlierTag(i, index) ? filteredData : originalData;

      /** identify values in filtered data, associated w/ tag field */
      dataValues.forEach(d => {
        const tagGroupValuesFromDoc = d[t.field];
        if (tagGroupValuesFromDoc && tagGroupValuesFromDoc.length > 0) {
          allActiveTags[t.field].push(tagGroupValuesFromDoc);
        }
      });

      allActiveTags[t.field] = allActiveTags[t.field].flat().filter(onlyUnique).map(normalizeTagString);

    });

    /**
     * Use the map to identify which of the options to display within a field, based on the filtered documents
     */
    this.configuration.filterTags.forEach((t, i) => {

      if (neitherSelfNorFirstNorEarlierTag(i, index)) {

        t.options.forEach(o => {

          /** No active tags, show no values */
          if (!allActiveTags[t.field] || allActiveTags[t.field].length === 0) {
            o.isHiddenBySubordinateLogic = true;
          } else {

            const explodedOptionValue = o.value.split('/');
            const finalStringInTag = explodedOptionValue[explodedOptionValue.length - 1];
            const lowerCaseFilterTagValue = normalizeTagString(finalStringInTag);

            o.isHiddenBySubordinateLogic = allActiveTags[t.field].indexOf(lowerCaseFilterTagValue) === -1;

          }

        })

      }

    });

  }

  /** Generates options for use in SELECTS and SELECTION-LISTS and RADIOs */
  parseOptionsFromTags(tags: string[], controlType: CONTROL_TYPE_ENUM): TagOption[] {

    const options: TagOption[] = tags.map(t => {
      return {
        label: t.substring(t.lastIndexOf('/') + 1, t.length).toUpperCase().replace(/-/g, ' '),
        value: t,
        isHiddenBySubordinateLogic: false
      }
    });

    // Sort checkbox list to make it look nice ( styling )
    if (controlType === CONTROL_TYPE_ENUM.MULTI_CHECKBOX) {
      return options.sort((a, b) => a.label.length - b.label.length);
    }

    return options;

  }

  valueIsNotNullOrEmpty(v: string | string[]) {
    return (v as string) || (v && (v as string[]).length !== 0);
  }

  /** Determine the root of the tags from their sub-values */
  getTagFieldNameFromOptions(tags: string[]) {
    // Only uses the first item, make sure the tags are all under the same folder structure 
    // @TODO - there's got to be a way to bucket and nest tags and use them [ instead of manually adding a hundred tags? ]
    if (tags && tags.length > 0) {
      const t = tags[0];
      const split = t.split('/');
      try {
        return split[split.length - 2];
      } catch (e) {
        console.error('Seems the depth of the tag might be underestimated - this code snags the parent of the tags: shopx:asdf1/-->asdf2<--/asdf3')
      }
    }
  }

  saveDocument(document: AEMDocument) {
    window.open(document.filePath, '_blank');
  }

  /** Necessary to have VIEW reflect PROGRAMMATIC setting of values */
  compareWithFn(obj1: TagOption, obj2: TagOption) {
    return obj1 && obj2 && obj1.value === obj2.value;
  }

  trackByIndex(index: number, obj: { label: string; value: string; }): number {
    return index;
  }
}
